import { memo, useState, type ComponentType } from 'react';
import type { WidgetPropsByType } from '@trinity/chat';
import { SegmentationDashboardWidget } from '@trinity/chat';
import { WidgetMediaCard } from '../Widget.Media/Widget.Media.Card.js';
import { WidgetQuoteCard } from '../Widget.Quote/Widget.Quote.Card.js';
import { WidgetTagSummaryCard } from '../Widget.TagSummary/Widget.TagSummary.Card.js';
import { RichTextUI } from '../Widgets/RichTextWidget.js';

type WidgetMap = {
  [K in SegmentationDashboardWidget]: ComponentType<WidgetPropsByType[K]>;
};

type ChartVisualizationProps = WidgetPropsByType[SegmentationDashboardWidget.ChartVisualization] & {
  hideLegend?: boolean;
  useCompactLegend?: boolean;
  isInteractive?: boolean;
};

const MediaThumbnail = memo((props: WidgetPropsByType[SegmentationDashboardWidget.Media]) => {
  const [, setMediaRef] = useState<HTMLMediaElement>(null);
  return (
    <WidgetMediaCard
      {...props}
      mediaSetting={props.media.type}
      disableMediaControls={true}
      setMediaRef={setMediaRef}
    />
  );
});

const QuoteThumbnail = memo((props: WidgetPropsByType[SegmentationDashboardWidget.Quote]) => {
  const [, setMediaRef] = useState<HTMLMediaElement>(null);
  return (
    <WidgetQuoteCard
      {...props}
      mediaSetting={props.media.type}
      disableMediaControls={true}
      setMediaRef={setMediaRef}
    />
  );
});

const RichTextThumbnail = memo((props: WidgetPropsByType[SegmentationDashboardWidget.RichText]) => (
  <RichTextUI content={props.value} />
));

export const TagSummaryWidget = memo((props: WidgetPropsByType[SegmentationDashboardWidget.TagSummary]) => {
  return <WidgetTagSummaryCard {...props} />;
});

export const QuoteWidget = memo((props: WidgetPropsByType[SegmentationDashboardWidget.Quote]) => {
  const [, setMediaRef] = useState<HTMLMediaElement>(null);

  return <WidgetQuoteCard {...props} mediaSetting={props.media.type} setMediaRef={setMediaRef} />;
});

export const MediaWidget = memo(
  (
    props: WidgetPropsByType[SegmentationDashboardWidget.Media] & {
      title?: string;
      blockTitle?: string;
      onClick?: () => void;
    }
  ) => {
    const [, setMediaRef] = useState<HTMLMediaElement>(null);

    return (
      <WidgetMediaCard
        {...props}
        title={props.title}
        mediaSetting={props.media.type}
        tags={props.tags}
        displayTags={props.displayTags}
        onClick={props.onClick}
        disableMediaControls={!!props.onClick}
        setMediaRef={setMediaRef}
      />
    );
  }
);

/**
 * Builds the widget maps shared by the apps. Only the chart component is app-specific
 * (it resolves theme colors from the app's own branding contexts), so each app passes its own.
 *
 * - `WIDGET_COMPONENT_MAP`: dashboards and selection modals (compact legend)
 * - `WIDGET_THUMBNAIL_MAP`: Widget Library previews (compact legend, non-interactive)
 */
export const createWidgetMaps = (ChartVisualization: ComponentType<ChartVisualizationProps>) => {
  const ChartVisualizationWidget = memo((props: WidgetPropsByType[SegmentationDashboardWidget.ChartVisualization]) => (
    <ChartVisualization {...props} hideLegend={false} useCompactLegend={true} />
  ));

  const ChartVisualizationThumbnail = memo(
    (props: WidgetPropsByType[SegmentationDashboardWidget.ChartVisualization]) => (
      <ChartVisualization {...props} hideLegend={false} useCompactLegend={true} isInteractive={false} />
    )
  );

  const WIDGET_COMPONENT_MAP: WidgetMap = {
    [SegmentationDashboardWidget.ChartVisualization]: ChartVisualizationWidget,
    [SegmentationDashboardWidget.Media]: MediaWidget as ComponentType<
      WidgetPropsByType[SegmentationDashboardWidget.Media]
    >,
    [SegmentationDashboardWidget.Quote]: QuoteWidget,
    [SegmentationDashboardWidget.RichText]: () => null,
    [SegmentationDashboardWidget.TagSummary]: TagSummaryWidget,
  };

  const WIDGET_THUMBNAIL_MAP: WidgetMap = {
    [SegmentationDashboardWidget.ChartVisualization]: ChartVisualizationThumbnail,
    [SegmentationDashboardWidget.Media]: MediaThumbnail as ComponentType<
      WidgetPropsByType[SegmentationDashboardWidget.Media]
    >,
    [SegmentationDashboardWidget.Quote]: QuoteThumbnail,
    [SegmentationDashboardWidget.RichText]: RichTextThumbnail,
    [SegmentationDashboardWidget.TagSummary]: TagSummaryWidget,
  };

  return { WIDGET_COMPONENT_MAP, WIDGET_THUMBNAIL_MAP };
};
